import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Shield, Brain, MessageSquare, Building2, Users, TrendingUp, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from 'axios';

const LandingPage = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    units_range: '',
    challenges: [],
    message: ''
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (challenge) => {
    setFormData(prev => {
      const challenges = prev.challenges.includes(challenge)
        ? prev.challenges.filter(c => c !== challenge)
        : [...prev.challenges, challenge];
      return { ...prev, challenges };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    // Mapping frontend display values to backend keys if needed, 
    // but here we used English keys in models.py enum, so we should send English keys.
    // Let's ensure the Select values are the English keys.
    
    try {
      await axios.post(`${API_URL}/api/leads`, formData);
      
      toast.success("Solicitud enviada", {
        description: "Gracias por tu interés. Nuestro equipo se pondrá en contacto contigo muy pronto para continuar con el proceso."
      });
      setOpen(false);
      setFormData({ 
        name: '', email: '', phone: '', role: '', units_range: '', challenges: [], message: '' 
      });
    } catch (error) {
      console.error(error);
      toast.error("Error al enviar solicitud", {
        description: "Por favor intenta de nuevo."
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-xl z-50 border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png" 
                alt="LivCom" 
                className="h-8 w-8 sm:h-10 sm:w-10"
              />
              <span className="text-xl sm:text-2xl font-heading font-bold text-primary hidden sm:block">LivCom</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('features')}
                className="text-text-secondary hover:text-primary font-medium transition-colors"
              >
                Características
              </button>
              <button 
                onClick={() => scrollToSection('ai-copilot')}
                className="text-text-secondary hover:text-primary font-medium transition-colors"
              >
                IA Copiloto
              </button>
              <button 
                onClick={() => scrollToSection('roles')}
                className="text-text-secondary hover:text-primary font-medium transition-colors"
              >
                Roles
              </button>
              <button 
                onClick={() => scrollToSection('security')}
                className="text-text-secondary hover:text-primary font-medium transition-colors"
              >
                Seguridad
              </button>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-primary hover:bg-slate-50 font-medium px-3 sm:px-6 text-xs sm:text-base h-9 sm:h-10" data-testid="nav-login-button">
                  Ingresar
                </Button>
              </Link>
              <Button 
                onClick={() => setOpen(true)}
                className="bg-accent hover:bg-accent/90 text-white rounded-full px-4 sm:px-8 font-semibold shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl text-xs sm:text-base h-9 sm:h-10"
                data-testid="nav-register-button"
              >
                Acceso
                <span className="hidden sm:inline ml-1">demo</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 relative overflow-hidden" data-testid="hero-section">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 -z-10 w-1/2 h-full bg-gradient-to-l from-secondary/5 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-2xl">
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-heading font-bold text-slate-900 leading-[1.1] tracking-tight mb-8">
                Administra tus conjuntos con <span className="text-primary">inteligencia</span>.
              </h1>
              <p className="text-lg sm:text-xl text-slate-500 mb-6 leading-relaxed max-w-lg">
                LivCom centraliza comunicación, operación y finanzas. 
                El copiloto operativo para administradores que buscan control total y cero caos.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <Button 
                  size="lg"
                  onClick={() => setOpen(true)}
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-5 sm:px-10 sm:py-7 text-base sm:text-lg font-semibold shadow-xl shadow-primary/20 transition-all hover:-translate-y-1"
                  data-testid="hero-cta-primary"
                >
                  Solicitar acceso
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-slate-200 text-slate-600 hover:text-primary hover:border-primary/20 hover:bg-primary/5 rounded-full px-8 py-5 sm:px-10 sm:py-7 text-base sm:text-lg font-medium transition-all"
                  data-testid="hero-cta-secondary"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver demo
                </Button>
              </div>
              
              <div className="mt-12 flex items-center gap-4 text-sm text-slate-500">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="h-10 w-10 rounded-full border-2 border-white bg-slate-100 overflow-hidden">
                       <img src={`https://i.pravatar.cc/100?img=${i+10}`} alt="User" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
                <p>Confían en nosotros +50 administradores</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/50 border border-slate-100">
                <img 
                  src="https://images.unsplash.com/photo-1615406020658-6c4b805f1f30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBidWlsZGluZ3xlbnwwfHx8fDE3NjgyNzQzOTl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern residential building"
                  className="w-full h-[600px] object-cover hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-8">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-green-400 animate-pulse" />
                      <p className="font-medium">Sistema operando: 100%</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating Element */}
              <div className="absolute -left-12 bottom-20 bg-white rounded-xl p-4 shadow-xl border border-slate-50 hidden lg:block animate-bounce-slow">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">Estado actual</p>
                    <p className="text-sm font-bold text-slate-800">Todo bajo control</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-background" data-testid="problem-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-semibold text-primary mb-6">
            La administración residencial se volvió más compleja de lo necesario
          </h2>
          <p className="text-lg text-text-secondary leading-relaxed">
            WhatsApp saturado, información dispersa, procesos reactivos y poca trazabilidad clara. 
            Cuando administras varios conjuntos, el desorden escala más rápido que el control.
          </p>
          <p className="text-lg text-primary font-medium mt-6">
            LivCom nace para devolver claridad, orden y control a la operación diaria.
          </p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 px-6 bg-white" id="features" data-testid="value-prop-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-slate-900 mb-6">
              Un solo panel. Todos tus conjuntos. <br/>
              <span className="text-primary">Operación bajo control.</span>
            </h2>
            <p className="text-lg text-slate-500">
              Simplificamos lo complejo. LivCom transforma el caos de la administración en procesos claros y auditables.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                text: 'Gestión multi-conjunto unificada',
                desc: 'Cambia entre propiedades con un solo clic.'
              },
              {
                text: 'Adiós al WhatsApp masivo',
                desc: 'Canales oficiales y organizados por tema.'
              },
              {
                text: 'Priorización inteligente',
                desc: 'La IA te dice qué atender primero.'
              },
              {
                text: 'Historial inalterable',
                desc: 'Trazabilidad total de cada evento.'
              },
              {
                text: 'Reportes automáticos',
                desc: 'Genera informes de gestión en segundos.'
              },
              {
                text: 'Finanzas transparentes',
                desc: 'Control de pagos y gastos en tiempo real.'
              }
            ].map((item, index) => (
              <div key={index} className="group p-8 rounded-2xl bg-white border border-slate-100 hover:border-primary/20 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Check className="h-5 w-5 text-primary group-hover:text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.text}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Copilot Section */}
      <section className="py-24 px-6 bg-slate-50/50" id="ai-copilot" data-testid="ai-copilot-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-200 border border-slate-100">
                 <img 
                  src="https://images.unsplash.com/photo-1714859100409-42afa4af36ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxwcm9wZXJ0eSUyMG1hbmFnZXIlMjB0YWJsZXR8ZW58MHx8fHwxNzY4Mjc0NDA1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Property manager using tablet"
                  className="w-full h-full object-cover"
                />
                {/* Overlay UI Mockup */}
                <div className="absolute top-8 right-8 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg border border-slate-100 max-w-xs animate-pulse-slow">
                   <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Alerta de IA</p>
                        <p className="text-sm font-bold text-slate-800">Mantenimiento vencido</p>
                      </div>
                   </div>
                   <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full w-2/3 bg-orange-500 rounded-full" />
                   </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-full mb-6 border border-purple-100">
                <Brain className="h-4 w-4" />
                <span className="text-sm font-semibold tracking-wide uppercase">IA Integrada</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
                Tu copiloto operativo <br/>
                <span className="text-primary">inteligente y diario.</span>
              </h2>
              <p className="text-lg text-slate-500 mb-8 leading-relaxed">
                LivCom no solo guarda datos, los analiza. Recibe cada mañana un resumen ejecutivo con lo que realmente importa.
                Olvídate de buscar en chats de WhatsApp.
              </p>
              
              <div className="space-y-4">
                {[
                  'Detección automática de urgencias',
                  'Resumen de pagos pendientes',
                  'Alertas de mantenimiento preventivo'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-white border border-slate-200 rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
                <p className="text-slate-600 italic relative z-10">
                  "Hoy tienes <span className="font-bold text-red-500">3 PQRS críticas</span> y 
                  <span className="font-bold text-orange-500"> 1 mantenimiento vencido</span> en Conjunto Alameda."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 px-6" data-testid="capabilities-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-semibold text-primary text-center mb-4">
            Todo lo que necesitas para operar sin fricción
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:border-secondary hover:shadow-md transition-all">
              <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                <Building2 className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Operación</h3>
              <p className="text-text-secondary text-sm">PQRS · Mantenimiento · Paquetería · Emergencias</p>
            </div>
            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:border-secondary hover:shadow-md transition-all">
              <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Comunicación</h3>
              <p className="text-text-secondary text-sm">Avisos · Documentos · Historial auditable</p>
            </div>
            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:border-secondary hover:shadow-md transition-all">
              <div className="h-12 w-12 rounded-lg bg-status-success/10 flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-status-success" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Gestión</h3>
              <p className="text-text-secondary text-sm">Finanzas · Marketplace</p>
            </div>
            <div className="bg-white p-8 rounded-xl border border-slate-100 shadow-sm hover:border-secondary hover:shadow-md transition-all">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Brain className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Análisis IA</h3>
              <p className="text-text-secondary text-sm">Resúmenes inteligentes diarios</p>
            </div>
          </div>
          <p className="text-center text-text-secondary mt-8 text-sm">
            Todo desde una sola plataforma, adaptada por rol
          </p>
        </div>
      </section>

      {/* Multi-Role Section */}
      <section className="py-20 px-6 bg-background" id="roles" data-testid="multi-role-section">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-semibold text-primary mb-6">
            Una sola app que se adapta a cada rol
          </h2>
          <p className="text-lg text-text-secondary mb-12 max-w-3xl mx-auto">
            Administradores, residentes y proveedores usan la misma plataforma, 
            pero cada uno accede únicamente a lo que le corresponde.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Control total</h3>
              <p className="text-text-secondary">para el administrador</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="h-16 w-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Experiencia simple</h3>
              <p className="text-text-secondary">para el residente</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-3">Acceso puntual</h3>
              <p className="text-text-secondary">para proveedores</p>
            </div>
          </div>
          <p className="text-lg text-primary font-medium mt-12">
            Más adopción. Menos fricción.
          </p>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 px-6" id="security" data-testid="security-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-4xl font-heading font-semibold text-primary mb-6">
            Seguridad, trazabilidad y control real
          </h2>
          <p className="text-lg text-text-secondary leading-relaxed">
            Cada acción queda registrada. Cada proceso tiene historial. 
            Cada permiso está claramente definido. LivCom fue diseñada desde el inicio 
            para operar con confianza, respaldo y trazabilidad.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-primary to-primary/90 text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            Administra mejor. Decide con claridad.
          </h2>
          <p className="text-lg sm:text-xl mb-8 text-white/90">
            Descubre cómo LivCom puede transformar la forma en que gestionas tus conjuntos.
          </p>
          <Button 
            size="lg"
            onClick={() => setOpen(true)}
            className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-5 sm:px-12 sm:py-6 text-base sm:text-lg font-semibold shadow-2xl transition-all hover:-translate-y-0.5"
            data-testid="final-cta-button"
          >
            Solicitar acceso
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="text-sm text-white/70 mt-6">
            Un miembro del equipo se pondrá en contacto contigo.
          </p>
        </div>
      </section>

      {/* Access Request Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png" 
                alt="LivCom" 
                className="h-8 w-8"
              />
              <span className="text-xl font-heading font-bold text-primary">LivCom</span>
            </div>
            <DialogTitle className="text-2xl font-heading font-bold text-center text-slate-900">Solicitar acceso</DialogTitle>
            <DialogDescription className="text-center text-base pt-2 max-w-sm">
              Déjanos tus datos y te contactaremos para configurar tu cuenta en LivCom.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name" className="text-slate-700 font-medium">Nombre completo <span className="text-red-500">*</span></Label>
              <Input id="name" value={formData.name} onChange={handleInputChange} required placeholder="Juan Pérez" className="h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors" />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Correo electrónico corporativo <span className="text-red-500">*</span></Label>
                <Input id="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="juan@administracion.com" className="h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors" />
                <p className="text-xs text-slate-400">Usa un correo de trabajo para respuesta rápida.</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium">Teléfono <span className="text-red-500">*</span></Label>
                <Input id="phone" type="tel" value={formData.phone} onChange={handleInputChange} required placeholder="+57 300 000 0000" className="h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
               <div className="grid gap-2">
                <Label className="text-slate-700 font-medium">Rol del solicitante <span className="text-red-500">*</span></Label>
                <Select onValueChange={(val) => handleSelectChange('role', val)} required>
                  <SelectTrigger className="h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors">
                    <SelectValue placeholder="Selecciona tu rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="company">Empresa administradora</SelectItem>
                    <SelectItem value="council">Consejo de administración</SelectItem>
                    <SelectItem value="security">Seguridad / Portería</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label className="text-slate-700 font-medium">¿Cuántas unidades administras? <span className="text-red-500">*</span></Label>
                 <Select onValueChange={(val) => handleSelectChange('units_range', val)} required>
                  <SelectTrigger className="h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors">
                    <SelectValue placeholder="Selecciona un rango" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-50">1 – 50</SelectItem>
                    <SelectItem value="51-150">51 – 150</SelectItem>
                    <SelectItem value="151-500">151 – 500</SelectItem>
                    <SelectItem value="500+">Más de 500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3">
               <Label className="text-slate-700 font-medium">¿Cuál es hoy tu mayor reto? (Opcional)</Label>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {[
                    "PQRS y comunicación", 
                    "Control financiero", 
                    "Reservas y áreas comunes", 
                    "Paquetería", 
                    "Mantenimiento", 
                    "Seguridad y emergencias",
                    "Todo está desordenado"
                  ].map((challenge, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <Checkbox id={`c-${idx}`} onCheckedChange={() => handleCheckboxChange(challenge)} className="mt-0.5 border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                      <label htmlFor={`c-${idx}`} className="text-sm font-medium leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-slate-600 cursor-pointer">
                        {challenge}
                      </label>
                    </div>
                  ))}
               </div>
            </div>

            <div className="grid gap-2">
               <Label htmlFor="message" className="text-slate-700 font-medium">Mensaje (Opcional)</Label>
               <Textarea id="message" value={formData.message} onChange={handleInputChange} placeholder="Cuéntanos brevemente qué necesitas..." className="min-h-[80px] bg-slate-50 border-slate-200 focus:bg-white transition-colors" />
            </div>

            <div className="pt-2">
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-6 text-lg rounded-xl shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar solicitud'
                )}
              </Button>
              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-400 bg-slate-50 py-2 rounded-lg">
                <Lock className="h-3 w-3" />
                <p>LivCom es una plataforma privada. Revisamos cada solicitud personalmente.</p>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-primary text-white py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png" 
              alt="LivCom" 
              className="h-8 w-8"
            />
            <h3 className="text-xl font-heading font-bold">LivCom</h3>
          </div>
          <p className="text-white/60 text-sm">
            Control Operativo Residencial - Diseñado para LATAM
          </p>
          <p className="text-white/40 text-xs mt-4">
            © 2025 LivCom. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;